// Detects if a job is a pest / rodent WO and offers a one-tap Trap Placement
// Guide download for the tech. Guesses pest species from category + title +
// description text; if we can't guess we show all guides in a dropdown.

import { useMemo, useState, useEffect } from "react";
import { FilePdf, CaretDown } from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;

const PEST_KEYWORDS = [
  ["cockroach", ["cockroach", "roach"]],
  ["bed_bug", ["bed bug", "bedbug", "bed-bug"]],
  ["mouse", ["mouse", "mice"]],
  ["rat", ["rat "]],
  ["ant", ["ant "]],
  ["termite", ["termite"]],
  ["silverfish", ["silverfish"]],
  ["spider", ["spider"]],
  ["fly", ["fruit fly", "house fly", " fly "]],
  ["wasp_bee", ["wasp", "hornet", "bee "]],
  ["flea", ["flea"]],
];

function guessPest(job) {
  const hay = `${job.title || ""} ${job.category || ""} ${job.description || ""}`.toLowerCase();
  for (const [key, needles] of PEST_KEYWORDS) {
    if (needles.some(n => hay.includes(n))) return key;
  }
  return null;
}

function isPestJob(job) {
  const hay = `${job.category || ""} ${job.title || ""}`.toLowerCase();
  return /pest|rodent|extermin|bug/.test(hay) || !!guessPest(job);
}

export default function PestTrapGuidePanel({ job }) {
  const [guides, setGuides] = useState([]);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);

  const guessed = useMemo(() => guessPest(job), [job]);

  useEffect(() => {
    if (!isPestJob(job)) return;
    fetch(`${BASE}/api/pest-trap-guide`)
      .then(r => r.json())
      .then(g => setGuides(g || []))
      .catch(() => {});
    setSelected(guessed);
  }, [job, guessed]);

  if (!isPestJob(job)) return null;

  const label = guides.find(g => g.key === selected)?.title || "Pick a pest species…";

  return (
    <div className="mt-3 p-3 border-2 border-amber-500 bg-amber-50" data-testid="pest-trap-guide-panel">
      <div className="flex items-start gap-3">
        <div className="text-3xl leading-none pt-1" aria-hidden="true">🪳</div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-black uppercase tracking-widest text-amber-800">
            Pest / Rodent Job · Trap Placement Guide
          </div>
          <p className="text-xs text-slate-700 mt-1">
            One-page tech reference: where to place traps, what bait, dwell time, safety notes.
            {guessed ? " Guessed the pest from the job description — change it below if wrong." : " Pick the pest species below to open the guide."}
          </p>
          <div className="mt-2 relative inline-block w-full max-w-md">
            <button onClick={() => setOpen(o => !o)} data-testid="pest-guide-picker"
              className="w-full h-10 px-3 border-2 border-[#0f172a] bg-white text-left text-xs font-black uppercase tracking-widest flex items-center justify-between hover:bg-slate-50">
              <span className="truncate">{label}</span>
              <CaretDown size={14}/>
            </button>
            {open && (
              <div className="absolute z-10 top-full mt-1 left-0 right-0 bg-white border-2 border-[#0f172a] max-h-64 overflow-y-auto shadow-lg">
                {guides.map(g => (
                  <button key={g.key} onClick={() => { setSelected(g.key); setOpen(false); }}
                    data-testid={`pest-guide-opt-${g.key}`}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-amber-100 border-b border-slate-200 ${selected === g.key ? "bg-amber-50 font-black" : ""}`}>
                    {g.title}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="mt-2">
            <a href={selected ? `${BASE}/api/pest-trap-guide/${selected}.pdf` : "#"}
               target="_blank" rel="noreferrer"
               onClick={e => { if (!selected) { e.preventDefault(); setOpen(true); } }}
               data-testid="pest-guide-download"
               className={`inline-flex items-center gap-1 h-10 px-3 border-2 border-[#0f172a] font-black uppercase text-xs tracking-widest ${
                 selected ? "bg-amber-500 hover:bg-amber-600 text-white" : "bg-slate-200 text-slate-500 cursor-not-allowed"
               }`}>
              <FilePdf weight="fill" size={14}/>
              {selected ? "Download Guide PDF" : "Pick a pest first"}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
